import { I18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { palette, color, hslaConverter } from '@conserveit/blocks'
import { DataSource, DataSourceLegacy } from '../../../components/Reports/Utilities/types'
import { SortBy, UtilityConsumption, UtilityGroup, TimeFrame, MetricsType } from '../../../context/reports/utilities/types'
import { Sorting } from '../../../context/reports/types'
import { getSiteById } from '../../../utils/state'

/**
 * Retrieve sorted top keys based on acumulated consumption or key name
 * @param data
 * @param sortOption LatestConsumption option is not suitable for `YearInSites` and `MonthInSites` structured data
 * @param sortDirection
 */
export function getSortedKeys (data: UtilityGroup, sortOption: SortBy, sortDirection: Sorting): string[] {
  // Define a reversible flag for sort function
  const flag = sortDirection === Sorting.Desc ? -1 : 1

  // Sort by acumulated consumption
  if (sortOption === SortBy.TotalConsumption || sortOption === SortBy.LatestConsumption) {
    return Object.entries(data)
      // The total or latest consumption is required for each top key first
      .map(([key, consumption]) => {
        const entries = Object.values(consumption)
        return [
          key,
          entries.reduce((acc, num, index) => {
            // The down key here could be a month, a year or a siteId.
            // Keys are already sorted ascending unless siteIds. It's how JS behaves regarding numeric keys.
            // If sort option is TotalConsumption, accumulate all items,
            // Otherwise accumulate last items only which is last month or year or siteId
            return (sortOption === SortBy.TotalConsumption || index + 1 === entries.length) ? (acc + num) : acc
          }, 0)
        ]
      })
      // sort by accumulated consumption based on top key
      .sort((a, b) => a[1] > b[1] ? flag : (flag * -1))
      // pick sorted top keys as an array of strings
      .map((entry) => entry[0]) as string[]
  }

  // Sort by top key name regardless of consumption
  return Object.keys(data).sort((a, b) => a > b ? flag : (flag * -1))
}

/**
 * Get chart data source for ranked years
 * @param data
 * @param yearsSortingDirection
 */
export function getRankedYearsConfig (
  data: UtilityConsumption
): DataSource {
  const categories = [] as any
  const series = [] as any

  Object.entries(data['AllMonthInSites']).map(([date, items]) => {
    categories.push(date)

    Object.entries(items).forEach(([siteId, value]) => {
      const label = getSiteById(siteId).label
      const siteInSeries = series.find((obj: { name: string }) => {
        return obj.name === label
      })
      if(siteInSeries)
      {
        siteInSeries.data.push(value)
      }
      else
      {
        series.push({
          name: label,
          data: [value],
          color: getSiteColour(series.length)
        })
      }
    })
  })

  return {
    categories,
    series
  }
}

/**
 * Get chart data source for ranked years
 * @param data
 * @param sitesSortingDirection
 * @param yearsSortingDirection
 */
export function getRankedSitesConfig (
  data: UtilityConsumption,
  metricsType: MetricsType,
  sitesSortingDirection: Sorting,
  yearsSortingDirection: Sorting
): DataSource {

  const siteIds = getSortedKeys(data['SiteInYears'], SortBy.LatestConsumption, sitesSortingDirection)
  const years = getSortedKeys(data['YearInSites'], SortBy.Key, yearsSortingDirection)

  const categories = siteIds.map((siteId) => (
    getSiteById(siteId).label
  ))

  const series = years.map((year, index) => (
    {
      name: year,
      data: siteIds.map((siteId) => (
        data['YearInSites'][year][siteId] ? data['YearInSites'][year][siteId] : 0)),
      color: getColour(index, metricsType),
    }
  ))

  return {
    categories,
    series
  }
}

/**
 * Get chart data source for monthly resolution
 * @param data
 * @param yearsSortingDirection
 */
export function getSingleSiteConfig (
  data: UtilityConsumption,
  metricsType: MetricsType,
  yearsSortingDirection: Sorting
): DataSourceLegacy {
  const months = Object.keys(data['MonthInSites'])
  const years = getSortedKeys(data['YearInMonths'], SortBy.Key, yearsSortingDirection)

  return {
    categories: [{
      category: months.map((month) => ({ label: getMonthName(month) }))
    }],
    dataset: years.map((year, index) => ({
      seriesname: year,
      // showValues: true,
      color: getColour(index, metricsType),
      data: months.map((month) => ({
        value: data['YearInMonths'][year][month] ? data['YearInMonths'][year][month] : 0
      }))
    }))
  }
}

export const getTimeFrameList = (i18n: I18n, sliceCount?: number) => {
  const list = [
    {
      value: TimeFrame.YearToDate,
      label: i18n._(t('time.year_to_date')`Year to date`)
    },
    {
      value: TimeFrame.Last2Years,
      label: i18n._(t('time.last_two_years')`Last 2 years (inc. YTD)`)
    },
    {
      value: TimeFrame.Last3Years,
      label: i18n._(t('time.last_three_years')`Last 3 years (inc. YTD)`)
    }
  ]

  return sliceCount ? list.slice(0, sliceCount) : list
}

/**
 * Get timeframe range
 * @param timeFrameOption number/enum of selectable months to request
 */
export const getTimeFrameRange = (timeFrameOption: TimeFrame) => {
  const year = new Date().getFullYear() - timeFrameOption
  const startDate = new Date(year, 0, 1, 12, 0, 0).toISOString()
  const endDate = new Date().toISOString()

  return { timeFrameOption, startDate, endDate }
}

/**
 * Get three letters abbreviation of a month number
 * @param month month number starting from 1
 */
export function getMonthName (month: string) {
  month = month.replace(/^0+/, '') // Remove leading zero
  const list = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const index = parseInt(month, 10) - 1

  return list[index]
}

// Saturated and dark enough to stay legible as a thin bar on white.
const SITE_SATURATION = 65
const SITE_LIGHTNESS = 45
// Successive multiples of the golden angle never land near each other, so the
// hues stay well separated at any series count (>=20 degrees apart for 11
// sites) without needing to know the count up front.
const GOLDEN_ANGLE_DEGREES = 137.508

/**
 * Pick a chart colour for the nth site.
 *
 * Sites are chosen by the user, so the count is unbounded and a fixed palette
 * would run out. Spacing hues by the golden angle keeps them distinguishable
 * however many are selected, and leaves earlier sites untouched as more are
 * added -- site 3 is always the same colour whether 4 or 40 are on screen.
 */
export function getSiteColour (index: number) {
  const hue = (index * GOLDEN_ANGLE_DEGREES) % 360
  return `hsl(${hue.toFixed(1)}, ${SITE_SATURATION}%, ${SITE_LIGHTNESS}%)`
}

/**
 * Retrieve hexadecimal colour code based on an index number
 * @param index
 */
function getColour (index: number, metricsType: MetricsType) {
  const colours = getTypeColours(metricsType)
  const list = [
    colours.primary,
    colours.secondary,
    colours.tertiary
  ]
  if (index < 0 || index > 2) {
    index = 0
  }

  return list[index]
}

/**
 * Retrieve all colours needed for a metrics type
 * @param type
 */
export function getTypeColours (type: MetricsType) {
  const ink = hslaConverter(color.theme.typography.ink, 'hex')

  switch (type) {
    case MetricsType.Gas:
      return {
        ink,
        canvas: hslaConverter(palette.purple1, 'hex'),
        primary: hslaConverter(palette.purple8, 'hex'),
        secondary: hslaConverter(palette.purple5, 'hex'),
        tertiary: hslaConverter(palette.purple3, 'hex')
      }
    case MetricsType.Water:
      return {
        ink,
        canvas: hslaConverter(palette.blue2, 'hex'),
        primary: hslaConverter(palette.blue7, 'hex'),
        secondary: hslaConverter(palette.blue5, 'hex'),
        tertiary: hslaConverter(palette.blue3, 'hex')
      }
    case MetricsType.Electricity:
    default:
      return {
        ink,
        canvas: hslaConverter(palette.orange1, 'hex'),
        primary: hslaConverter(palette.orange8, 'hex'),
        secondary: hslaConverter(palette.orange6, 'hex'),
        tertiary: hslaConverter(palette.orange4, 'hex')
      }
  }
}
